import type { GovernedLibraryDocumentSummary } from "../api/cognitoryLibraryClient";
import {
  LIBRARY_CANONICAL_ACTIVATING_LABEL,
  LIBRARY_CANONICAL_BUILDING_LIBRARY_LABEL,
  LIBRARY_INGESTION_STILL_PROCESSING_LABEL,
  resolveLibraryItemUserVisibleStatus,
} from "./cognitoryLibraryIngestionStatus";
import {
  resolveUploadScopedDocumentSummary,
  type UploadAttemptLifecycle,
} from "./cognitoryLibraryUploadLifecycle";

export type UploadProgressStage =
  | "uploading"
  | "preparing"
  | "structuring"
  | "indexing"
  | "ready"
  | "failed";

export type UploadProgressPresentation = {
  stage: UploadProgressStage;
  /** Current stage headline without repeating the filename. */
  stageText: string;
  secondaryText: string | null;
};

const STRUCTURING_VERSION_STATUSES = new Set([
  "structure_chunked",
  "parsing",
  "chunked",
]);

const INDEXING_VERSION_STATUSES = new Set(["embedded"]);

export function resolveUploadProgressStage(input: {
  httpUploading: boolean;
  document?: GovernedLibraryDocumentSummary | null;
  uploadLifecycle?: UploadAttemptLifecycle | null | undefined;
}): UploadProgressStage {
  if (input.httpUploading) {
    return "uploading";
  }

  const document = resolveUploadScopedDocumentSummary(
    input.document ?? null,
    input.uploadLifecycle,
  );
  if (!document) {
    return "preparing";
  }

  const status = resolveLibraryItemUserVisibleStatus({
    canonicalState: document.canonicalState,
    versionStatus: document.versionStatus,
  });
  if (status === "ready") {
    return "ready";
  }
  if (status === "processing_failed") {
    return "failed";
  }

  if (document.canonicalState === "building_library") {
    return "indexing";
  }
  if (document.canonicalState === "activating") {
    return "indexing";
  }
  if (document.canonicalState === "indexed_not_serving") {
    return "indexing";
  }

  const versionStatus = document.versionStatus;
  if (!versionStatus || versionStatus === "source_recorded") {
    return "preparing";
  }
  if (STRUCTURING_VERSION_STATUSES.has(versionStatus)) {
    return "structuring";
  }
  if (INDEXING_VERSION_STATUSES.has(versionStatus)) {
    return "indexing";
  }
  if (
    versionStatus === "vector_indexed" ||
    versionStatus === "indexed" ||
    versionStatus === "verified"
  ) {
    return "ready";
  }
  if (versionStatus === "failed") {
    return "failed";
  }

  return "indexing";
}

export function resolveUploadProgressPresentation(input: {
  filename: string;
  httpUploading: boolean;
  document?: GovernedLibraryDocumentSummary | null;
  timedOut?: boolean;
  uploadLifecycle?: UploadAttemptLifecycle | null | undefined;
}): UploadProgressPresentation {
  if (input.timedOut) {
    return {
      stage: "indexing",
      stageText: LIBRARY_INGESTION_STILL_PROCESSING_LABEL,
      secondaryText:
        "Cognitory is still working on your document. You can stay on this page while it finishes.",
    };
  }

  const stage = resolveUploadProgressStage({
    httpUploading: input.httpUploading,
    document: input.document ?? null,
    uploadLifecycle: input.uploadLifecycle,
  });

  if (stage === "uploading") {
    return {
      stage,
      stageText: "Uploading…",
      secondaryText: "Your file is being sent securely to Cognitory.",
    };
  }

  if (stage === "preparing") {
    return {
      stage,
      stageText: "Preparing your document…",
      secondaryText:
        "Your document has been received. Cognitory is preparing it for grounded answers.",
    };
  }

  if (stage === "structuring") {
    return {
      stage,
      stageText: "Reading and structuring your document…",
      secondaryText:
        "This can take a little while for larger PDFs. You can stay on this page while Cognitory finishes.",
    };
  }

  if (stage === "indexing") {
    const canonicalLabel =
      input.document?.canonicalState === "building_library"
        ? LIBRARY_CANONICAL_BUILDING_LIBRARY_LABEL
        : input.document?.canonicalState === "activating"
          ? LIBRARY_CANONICAL_ACTIVATING_LABEL
          : "Indexing your document…";

    return {
      stage,
      stageText: canonicalLabel,
      secondaryText:
        "This can take a little while for larger PDFs. You can stay on this page while Cognitory finishes.",
    };
  }

  if (stage === "ready") {
    return {
      stage,
      stageText: "Ready",
      secondaryText:
        "You can now ask Cognitory questions grounded in this document.",
    };
  }

  return {
    stage: "failed",
    stageText: "Processing failed",
    secondaryText: "Try uploading it again.",
  };
}

export function resolveUploadFailureMessage(input: {
  filename: string;
  kind:
    | "forbidden"
    | "pdf_rejected"
    | "document_collision"
    | "network"
    | "unknown";
}): string {
  if (input.kind === "forbidden") {
    return "You do not have permission to upload into this library.";
  }
  if (input.kind === "pdf_rejected") {
    return `We couldn't finish preparing ${input.filename}. That file could not be admitted as a governed PDF.`;
  }
  if (input.kind === "document_collision") {
    return `This governed document already exists in the library.`;
  }
  return `We couldn't finish preparing ${input.filename}. Try uploading it again.`;
}

export function isUploadProgressTerminal(stage: UploadProgressStage): boolean {
  return stage === "ready" || stage === "failed";
}
